import React, { useState, ReactNode, useRef, useCallback, memo } from 'react';
import { MouseKey } from 'constants/types';

interface Props {
  children: ReactNode;
  isShiftKeyPressed: boolean;
  isDragSelect: boolean;
  setIsDragSelect: React.Dispatch<React.SetStateAction<boolean>>;
  onSelectionChange?: (selectedElements: number[]) => void;
}

/**
 * A React component that provides a drag selection box functionality for selecting elements within a container.
 *
 * @param props - The properties passed to the component.
 * @param props.children - The child elements to be rendered inside the container.
 * @param props.isShiftKeyPressed - A boolean indicating whether the Shift key is pressed.
 * @param props.isDragSelect - A boolean indicating whether drag selection is currently active.
 * @param props.setIsDragSelect - A function to update the drag selection state.
 * @param props.onSelectionChange - A callback function triggered when the selection changes, receiving an array of selected element IDs.
 *
 * @returns A container element that supports drag selection functionality.
 *
 * @remarks
 * - The component uses a `useRef` to reference the container DOM element.
 * - The drag selection box is rendered dynamically based on mouse events.
 * - Elements to be selected must have the class `selectable-item` and a `data-id` attribute containing their unique ID.
 * - The selection rectangle is styled with a semi-transparent blue background and a solid border.
 *
 * @example
 * ```tsx
 * <DragSelectionBox
 *   isShiftKeyPressed={isShiftKeyPressed}
 *   isDragSelect={isDragSelect}
 *   setIsDragSelect={setIsDragSelect}
 *   onSelectionChange={(selectedIds) => console.log(selectedIds)}
 * >
 *   <div className="selectable-item" data-id="1">Item 1</div>
 *   <div className="selectable-item" data-id="2">Item 2</div>
 * </DragSelectionBox>
 * ```
 */
const DragSelectionBox = (props: Props) => {
  const {
    children,
    isShiftKeyPressed,
    isDragSelect,
    setIsDragSelect,
    onSelectionChange,
  } = props;
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [currentPoint, setCurrentPoint] = useState<{ x: number; y: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectElements = useCallback(
    (selectionRect: { x: number; y: number; width: number; height: number }) => {
      const selectableElements =
        containerRef.current?.querySelectorAll('.selectable-item') || [];
      const selectedElements: number[] = [];

      selectableElements.forEach((el) => {
        const box = el.getBoundingClientRect();
        const containerBox = containerRef.current?.getBoundingClientRect();
        if (!containerBox) {
          return;
        }

        const elementRect = {
          left: box.left - containerBox.left,
          top: box.top - containerBox.top,
          right: box.right - containerBox.left,
          bottom: box.bottom - containerBox.top,
        };
        const isIntersecting =
          selectionRect.x < elementRect.right &&
          selectionRect.x + selectionRect.width > elementRect.left &&
          selectionRect.y < elementRect.bottom &&
          selectionRect.y + selectionRect.height > elementRect.top;

        if (isIntersecting) {
          const nodeId = parseInt(el?.attributes?.['data-id']?.value);
          if (nodeId) {
            selectedElements.push(nodeId);
          }
        }
      });

      if (onSelectionChange) {
        onSelectionChange(selectedElements);
      }
    },
    [onSelectionChange],
  );

  const handleMouseDown = (e: React.MouseEvent) => {
    const container = containerRef.current;
    if (!container || !isShiftKeyPressed) {
      return;
    }

    const rect = container.getBoundingClientRect();
    setIsDragSelect(true);
    setStartPoint({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setCurrentPoint({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragSelect || !containerRef.current) {
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const newPoint = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      setCurrentPoint(newPoint);

      if (startPoint) {
        const selectionRect = {
          x: Math.min(startPoint.x, newPoint.x),
          y: Math.min(startPoint.y, newPoint.y),
          width: Math.abs(startPoint.x - newPoint.x),
          height: Math.abs(startPoint.y - newPoint.y),
        };
        selectElements(selectionRect);
      }
    },
    [isDragSelect, startPoint, selectElements],
  );

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (isDragSelect && (e.buttons !== MouseKey.Left || !isShiftKeyPressed)) {
      handleMouseUp();
    }
  };

  const handleMouseUp = () => {
    setIsDragSelect(false);
    setStartPoint(null);
    setCurrentPoint(null);
  };

  const getRectangleStyle = () => {
    if (!startPoint || !currentPoint) {
      return {};
    }
    const x = Math.min(startPoint.x, currentPoint.x);
    const y = Math.min(startPoint.y, currentPoint.y);
    const width = Math.abs(startPoint.x - currentPoint.x);
    const height = Math.abs(startPoint.y - currentPoint.y);
    return {
      position: 'absolute' as const,
      left: x,
      top: y,
      width,
      height,
      backgroundColor: 'rgba(0, 123, 255, 0.3)',
      border: '1px solid rgba(0, 123, 255, 0.8)',
      pointerEvents: 'none',
    } as React.CSSProperties;
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseEnter={handleMouseEnter}
    >
      {children}
      {isDragSelect && <div style={getRectangleStyle()} />}
    </div>
  );
};

export default memo(DragSelectionBox);
